import { SPLIT_TESTING_KEY, USER_ID_KEY } from '$lib/global/constants';

import Cookies from 'js-cookie';

/**
 * Cookies written by Segment's analytics.js core. These are the identifiers the German
 * Competition Centre flagged in CAP-4147 — after the visitor rejects consent they must
 * be *gone*, not merely no longer updated.
 */
const TRACKING_COOKIES = [
    'ajs_anonymous_id',
    'ajs_user_id',
    'ajs_group_id',
    'ajs_group_properties',
];

/**
 * Exact storage keys holding tracking identity.
 *
 * Applied to *both* localStorage and sessionStorage: Segment mirrors `ajs_anonymous_id` /
 * `ajs_user_id` into sessionStorage as well as into cookies and localStorage (verified in
 * the browser), and leaving either copy behind means the identifier the cease-and-desist
 * names is still on the visitor's device after they reject.
 */
const TRACKING_IDENTITY_KEYS = [
    USER_ID_KEY,
    'ajs_anonymous_id',
    'ajs_user_id',
    'ajs_group_id',
    'ajs_group_properties',
    'ajs_traits',
];

const RETRY_QUEUE_SUFFIXES = ['ack', 'inProgress', 'queue', 'reclaimStart', 'reclaimEnd'];

/**
 * The @segment/localstorage-retry queue, created as `new Queue('perspective', ...)` in
 * global/core/queue.ts. It holds not-yet-delivered /ps-fnl-events payloads, i.e. visitor
 * data, so it has to go when consent is withdrawn.
 *
 * The library inserts a per-tab UUID between the queue name and the suffix, producing keys
 * like `perspective.7734c7c5-be6d-44b5-9a4b-5eb8b73f7921.queue`. Our cookie policy documents
 * them as plain `perspective.queue` / `perspective.ack` — that is not what actually lands in
 * localStorage (verified in the browser), so match on the suffix to cover both shapes.
 */
const isRetryQueueKey = (key: string): boolean =>
    key.startsWith('perspective.') &&
    RETRY_QUEUE_SUFFIXES.some((suffix) => key.endsWith(`.${suffix}`));

/**
 * sessionStorage prefixes for tracking state.
 *
 * Deliberately NOT purged, because these are not tracking identifiers and the funnel
 * needs them to work:
 * - `perspective.tracking-preferences.` — the consent decision itself
 * - `perspective.result-mapping.`       — quiz answer → result page mapping
 * - `perspective.tracking-properties.`  — text personalization from the visitor's own answers
 */
const TRACKING_SESSION_STORAGE_PREFIXES = [
    SPLIT_TESTING_KEY,
    'perspective_campaign_session_',
    'perspective_seen_non_homepage_',
];

/** Segment persists per-session user traits under `<sessionId>_user_traits`. */
const USER_TRAITS_SUFFIX = '_user_traits';

const safeKeys = (storage: Storage): string[] => {
    try {
        const keys: string[] = [];

        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);

            if (key !== null) {
                keys.push(key);
            }
        }

        return keys;
    } catch {
        // Storage unavailable (Safari private mode, cross-origin iframe)
        return [];
    }
};

const safeRemove = (storage: Storage, key: string): void => {
    try {
        storage.removeItem(key);
    } catch {
        // Storage unavailable
    }
};

const isTrackingKey = (key: string, prefixes: string[]): boolean =>
    key.endsWith(USER_TRAITS_SUFFIX) || prefixes.some((prefix) => key.startsWith(prefix));

/**
 * Segment sets its cookies on the registrable domain, so a funnel served from
 * `go.example.de` gets cookies scoped to `.example.de`. js-cookie only deletes a cookie
 * when the delete request's domain matches the one it was written with, so walk every
 * candidate domain from the full host up to the registrable domain.
 */
const cookieDomainCandidates = (): (string | undefined)[] => {
    // `undefined` covers the host-only cookie (no explicit domain attribute).
    const domains: (string | undefined)[] = [undefined];
    const hostname = window.location?.hostname;

    if (!hostname) {
        return domains;
    }

    const parts = hostname.split('.');

    for (let i = 0; i < parts.length - 1; i++) {
        const domain = parts.slice(i).join('.');
        domains.push(domain, `.${domain}`);
    }

    return domains;
};

const removeCookie = (name: string): void => {
    for (const domain of cookieDomainCandidates()) {
        try {
            Cookies.remove(name, domain ? { path: '/', domain } : { path: '/' });
        } catch {
            // Ignore and keep trying the remaining domain scopes
        }
    }
};

/**
 * Delete every tracking cookie, storage entry and buffered event created by the funnel's
 * analytics stack.
 *
 * Called when the visitor denies consent (CAP-4147). Previously nothing was ever deleted:
 * "Reject all" left `ajs_anonymous_id`, `ajs_user_id` and `perspective__user__id` in place,
 * and they survived a reload.
 */
export const purgeTrackingArtifacts = (): void => {
    if (typeof window === 'undefined') return;

    TRACKING_COOKIES.forEach(removeCookie);

    try {
        const localStorage = window.localStorage;

        TRACKING_IDENTITY_KEYS.forEach((key) => safeRemove(localStorage, key));

        safeKeys(localStorage)
            // Segment falls back to localStorage for traits when its sessionStorage
            // proxy is unavailable (see trackingStorage.safeGetUserTraits).
            .filter((key) => key.endsWith(USER_TRAITS_SUFFIX) || isRetryQueueKey(key))
            .forEach((key) => safeRemove(localStorage, key));
    } catch {
        // localStorage unavailable
    }

    try {
        const sessionStorage = window.sessionStorage;

        TRACKING_IDENTITY_KEYS.forEach((key) => safeRemove(sessionStorage, key));

        safeKeys(sessionStorage)
            .filter(
                (key) =>
                    isTrackingKey(key, TRACKING_SESSION_STORAGE_PREFIXES) || isRetryQueueKey(key),
            )
            .forEach((key) => safeRemove(sessionStorage, key));
    } catch {
        // sessionStorage unavailable
    }

    // Drop events buffered by the analytics.js stub queue so a later consent grant
    // cannot retroactively flush anything collected while consent was pending.
    if (Array.isArray(window.analytics_queue)) {
        window.analytics_queue.length = 0;
    }

    if (Array.isArray(window.ps_queue)) {
        window.ps_queue.length = 0;
    }
};
